import { Link } from 'react-router-dom';
import type { RequirementEvaluationResponse, RoleFitResponse } from '../../api/types';
import {
  REQUIREMENT_LEVEL_COPY,
  REQUIREMENT_STATUS_COPY,
  ROLE_FIT_STATUS_COPY,
  SUBJECT_TYPE_COPY,
  describeEmployeeValue,
  describeThreshold,
  isAbsenceOfInformation,
  reasonLabel,
} from './roleDisplay';
import styles from './RoleFitBreakdown.module.css';

/**
 * One Role Fit result, rendered.
 *
 * ---------------------------------------------------------------------
 * THE PERCENTAGE IS NEVER THE ANSWER
 *
 * `docs/ROLE_FRAMEWORK.md` §4.2 requires the composite figure be shown
 * "alongside, never instead of, the per-requirement breakdown", and §4's
 * whole design puts `hasCriticalGap` outside the percentage precisely so a
 * high number can never imply "no critical issues". So the headline here is
 * three things read together — the figure, the coarse status, and the
 * critical-gap statement — and the breakdown is always present underneath,
 * never behind a disclosure.
 *
 * The figure is also framed for what it is: a level-weighted average over
 * only the requirements that could be evaluated. Requirements the system
 * could not evaluate are excluded from its denominator by the domain, which
 * means a person with very little recorded can score high on very little
 * evidence. Saying how many requirements the figure actually rests on is
 * the difference between a number and an honest number.
 *
 * NOTHING HERE IS COMPUTED
 *
 * Every status, reason, grouping, count and the percentage itself arrive
 * decided from the API. This component groups nothing the API did not group
 * and compares nothing. The counts below are counts of the arrays the API
 * sent, which is arithmetic about the response, not about the employee.
 *
 * ORDER
 *
 * `components` is rendered in the role's own requirement order, not sorted
 * by status. Sorting gaps to the top would editorialise — it would make the
 * shortfalls the shape of the page — and the API's `strengths`/`gaps`
 * groupings already exist for callers that want that framing.
 * ---------------------------------------------------------------------
 */
export function RoleFitBreakdown({
  fit,
  roleName,
  roleId,
}: {
  fit: RoleFitResponse;
  roleName: string;
  roleId: string;
}) {
  const evaluableCount = fit.components.length - fit.requirementsNotEvaluable.length;

  return (
    <div className={styles.body}>
      <div className={styles.headline}>
        <div className={styles.figure}>
          <span className={styles.percent}>{fit.overallFit}%</span>
          <span className={styles.statusLabel}>{ROLE_FIT_STATUS_COPY[fit.status]} fit</span>
        </div>

        <div className={styles.framing}>
          <p>
            Calculated across {evaluableCount} of {fit.components.length}{' '}
            {fit.components.length === 1 ? 'requirement' : 'requirements'} for {roleName}.
            {fit.requirementsNotEvaluable.length > 0
              ? ` ${fit.requirementsNotEvaluable.length} could not be evaluated and ${
                  fit.requirementsNotEvaluable.length === 1 ? 'is' : 'are'
                } left out of the figure rather than counted as unmet.`
              : ''}
          </p>
          <p className={styles.caveat}>
            Read this figure with the breakdown below, not instead of it. It compares one person against one role's
            thresholds; it is not a rating of the person and not a comparison with anyone else.
          </p>
        </div>
      </div>

      {/* A critical gap is a separate fact from the percentage and is stated
          as one — the domain computes it independently so that a high
          figure can never hide it. Neutral wording: it names the unmet
          requirement, it does not characterise the person. */}
      {fit.hasCriticalGap ? (
        <p className={styles.criticalGap} role="note">
          <strong>At least one required item is not met.</strong> This is recorded separately from the figure above,
          which can be high while a required item is still unmet. The items concerned are marked “Required” and
          “Gap” below.
        </p>
      ) : null}

      {/* A scroll shadow, not a media query: this wrapper is always the
          horizontal scroll container, and the edge shadow it draws (see
          .tableScroll) only appears where the table genuinely continues
          off-screen. At a width where the table already fits, nothing about
          this wrapper is visible — there is no breakpoint to get wrong. */}
      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <caption className={styles.caption}>
            Every requirement this role records, what it asks for, what is recorded for this person, and what the
            system concluded. Shown in the role's own order.
          </caption>
          <thead>
            <tr>
              <th scope="col">Requirement</th>
              <th scope="col">Category</th>
              <th scope="col">Level</th>
              <th scope="col">The role asks for</th>
              <th scope="col">Recorded for this person</th>
              <th scope="col">Outcome</th>
            </tr>
          </thead>
          <tbody>
            {fit.components.map((evaluation) => (
              <RequirementRow key={evaluation.requirement.id} evaluation={evaluation} />
            ))}
          </tbody>
        </table>
      </div>

      <p className={styles.footnote}>
        <Link to={`/roles/${roleId}`}>See what {roleName} requires</Link>
      </p>
    </div>
  );
}

function RequirementRow({ evaluation }: { evaluation: RequirementEvaluationResponse }) {
  const { requirement, employeeValue, status } = evaluation;
  const statusCopy = REQUIREMENT_STATUS_COPY[status];
  const levelCopy = REQUIREMENT_LEVEL_COPY[requirement.requirementLevel];
  const recorded = describeEmployeeValue(evaluation);
  const absence = isAbsenceOfInformation(status);

  return (
    <tr>
      <th scope="row">{requirement.subjectName}</th>
      <td>{SUBJECT_TYPE_COPY[requirement.subjectType].label}</td>
      <td>
        <span className={requirement.requirementLevel === 'required' ? styles.required : styles.preferred}
          title={levelCopy.meaning}
        >
          {levelCopy.label}
        </span>
      </td>
      <td>{describeThreshold(requirement)}</td>
      <td>
        {recorded === null ? (
          <span className={styles.absent}>Nothing recorded</span>
        ) : (
          <>
            {recorded}
            {/* docs/ROLE_FRAMEWORK.md §4.3: a requirement met on
                low-confidence data must not read as more certain than the
                data behind it. */}
            {requirement.subjectType === 'attribute' && employeeValue.attributeConfidenceLevel ? (
              <span className={styles.qualifier}> · {employeeValue.attributeConfidenceLevel} confidence</span>
            ) : null}
            {requirement.subjectType === 'qualification' && employeeValue.qualificationVerificationStatus ? (
              <span className={styles.qualifier}> · {employeeValue.qualificationVerificationStatus}</span>
            ) : null}
          </>
        )}
      </td>
      <td>
        <span
          className={`${styles.outcome} ${absence ? styles.outcomeAbsence : styles[`outcome_${status}`] ?? ''}`}
          title={statusCopy.meaning}
        >
          {statusCopy.label}
        </span>
        <span className={styles.reason}>{reasonLabel(evaluation.reason)}</span>
      </td>
    </tr>
  );
}
